<template>
  <protected :with="Permission.READ_DIAGNOSTICS" default-message>
    <div class="admin-diagnostics">
      <admin-view-header title="Diagnostyka systemu" />
      <tabs :tabs="tabs" v-model="tab">
        <template #metrics>
          <loader :state="actions.availableMetrics">
            <div class="field is-horizontal">
              <div class="field-label">
                <label class="label">Metryka</label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control has-icons has-icons-left">
                    <icon name="fa-ruler-combined" class="is-left" />
                    <div class="select">
                      <select v-model="selectedMetrics">
                        <option value disabled selected
                          >Wybierz opowiednią metrykę</option
                        >
                        <option
                          v-for="metrics in availableMetrics"
                          :key="metrics"
                          >{{ metrics }}</option
                        >
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <loader :state="actions.metrics">
              <template v-if="metrics.availableTags.length > 0">
                <div class="field is-horizontal">
                  <div class="field-label" />
                  <div class="field-body">
                    <strong>Dostępne tagi</strong>
                  </div>
                </div>
                <div
                  class="field is-horizontal"
                  v-for="tag in metrics.availableTags"
                  :key="tag.tag"
                >
                  <div class="field-label">
                    <label class="label">{{ tag.tag }}</label>
                  </div>
                  <div class="field-body">
                    <div class="field">
                      <div class="control has-icons has-icons-left">
                        <icon name="fa-tag" class="is-left" />
                        <div class="control">
                          <div class="select">
                            <select v-model="metricsTags[tag.tag]">
                              <option value selected class="has-text-grey"
                                >-- Ignoruj wartość --</option
                              >
                              <option
                                v-for="value in tag.values"
                                :value="value"
                                :key="value"
                                >{{ value }}</option
                              >
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="field is-horizontal">
                  <div class="field-label" />
                  <div class="field-body">
                    <div class="control">
                      <button
                        class="button is-success"
                        :class="{ 'is-loading': isActionLoading('metrics-') }"
                        :disabled="isActionLoading('metrics')"
                        @click="getMetricsWithTags"
                      >
                        <icon name="fa-undo" />
                        <span>Wykonaj zapytanie ponownie</span>
                      </button>
                    </div>
                  </div>
                </div>
              </template>
              <div class="metrics">
                <message-box
                  :title="metrics.name"
                  type="primary"
                  icon="fa-ruler-combined"
                >
                  <p :class="{ 'has-text-grey-light': !metrics.description }">
                    {{ metrics.description || "Opis metryki niedostępny" }}
                  </p>
                </message-box>

                <table-data
                  class="is-fullwidth"
                  :header="[
                    'Pomiar',
                    'Wartość' +
                      (metrics.baseUnit ? ` [${metrics.baseUnit}]` : '')
                  ]"
                  :data="metrics.measurements"
                  :data-fields="['statistic', 'value']"
                />
              </div>

              <template #loading>
                <div
                  class="spinner is-3by4 is-grey-lighter is-thick is-large is-centered"
                ></div>
              </template>

              <template #error>
                <div class="grey-message">
                  <icon name="fa-times-circle" />
                  <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
                </div>
              </template>
            </loader>

            <template #loading>
              <div
                class="spinner is-3by4 is-grey-lighter is-thick is-large is-centered"
              ></div>
            </template>

            <template #error>
              <div class="grey-message">
                <icon name="fa-times-circle" />
                <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
              </div>
            </template>
          </loader>
        </template>

        <template #custom>
          <validable-form @submit="submit">
            <label class="label">Zapytanie diagnostyczne</label>
            <control-code-editor
              v-model="query"
              ref="customQueryEditor"
              language="json"
              rules="required|is_uri"
            />
            <button
              class="button is-success"
              :class="{ 'is-loading': isActionLoading('query') }"
              :disabled="isActionLoading('query')"
            >
              <icon name="fa-play" />
              <span>Wykonaj zapytanie</span>
            </button>
          </validable-form>
          <loader :state="actions.query">
            <div class="response">
              <label class="label">Odpowiedź</label>
              <control-code-editor :value="diagnostics" readonly />
            </div>

            <template #loading>
              <div
                class="spinner is-3by4 is-grey-lighter is-thick is-large is-centered"
              ></div>
            </template>

            <template #error>
              <div class="grey-message">
                <icon name="fa-times-circle" />
                <p>Dane niedostępne.</p>
              </div>
            </template>
          </loader>
        </template>
      </tabs>
    </div>
  </protected>
</template>

<script>
import { mapState } from "vuex";

import {
  REQUEST_AVAILABLE_METRICS,
  REQUEST_METRICS,
  REQUEST_DIAGNOSTICS
} from ":actions";

import {
  Icon,
  Tabs,
  MessageBox,
  TableData,
  ValidableForm,
  ControlCodeEditor
} from "%helpers";
import AdminViewHeader from "%admin/components/global/AdminViewHeader";

import { Protected, WithDispatcherProgress } from "%mixins";

import { filterObject } from "%utils/object";

export default {
  name: "admin-diagnostics",

  mixins: [Protected, WithDispatcherProgress],

  components: {
    Icon,
    AdminViewHeader,
    Tabs,
    MessageBox,
    TableData,
    ValidableForm,
    ControlCodeEditor
  },

  data() {
    return {
      tab: "metrics",
      selectedMetrics: "",
      metricsTags: {},
      query: "/",

      tabs: {
        metrics: {
          icon: "fa-ruler-combined",
          text: "Metryki"
        },

        custom: {
          icon: "fa-cogs",
          text: "Niestandardowe"
        }
      }
    };
  },

  computed: mapState({
    availableMetrics: state => state.diagnostics.availableMetrics.sort(),
    metrics: state => state.diagnostics.metrics,
    diagnostics: state => JSON.stringify(state.diagnostics.diagnostics, null, 2)
  }),

  methods: {
    submit() {
      this.dispatchWithProgress("query", REQUEST_DIAGNOSTICS, {
        query: this.query
      });
    },

    getMetricsWithTags() {
      this.dispatchWithProgress("metrics", REQUEST_METRICS, {
        name: this.selectedMetrics,
        tags: filterObject(this.metricsTags, (k, v) => v.length > 0)
      });
    },

    getMetrics(name, tags) {
      this.dispatchWithProgress("metrics", REQUEST_METRICS, {
        name,
        tags
      }).then(
        response =>
          (this.metricsTags = response.data.availableTags.reduce(
            (acc, curr) => {
              acc[curr.tag] = "";
              return acc;
            },
            {}
          ))
      );
    }
  },

  beforeMount() {
    this.dispatchWithProgress("availableMetrics", REQUEST_AVAILABLE_METRICS);
  },

  watch: {
    tab(tab) {
      if (tab === "metrics") {
        this.dispatchWithProgress(
          "availableMetrics",
          REQUEST_AVAILABLE_METRICS
        );
      }
    },

    selectedMetrics(name) {
      if (name.length > 0) {
        this.getMetrics(name);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.metrics {
  margin-top: 5rem;
}

.response {
  margin-top: 5rem;
}
</style>
